import { Button, createStyles, TextInput, Text, Group, Space, Select } from "@mantine/core";
import { useImperativeHandle, useState } from "react";
import t from "../../../stores/language";
import { useStore } from "../../../stores/store";
import { useForm } from "@mantine/form";
import theme from "../../../stores/theme";
import { observer } from "mobx-react";
import { User } from "../../../models/models";
import React from "react";
import Modal from "../../../custom-components/model";
const useStyles = createStyles((theme) => ({
  addUserForm: {
    padding: "0px 12px",
    paddingTop: theme.other.arc.paddings[0],
  },
  userFormFooter: {
    borderTop: "1px solid #ccc",
    padding: theme.other.arc.paddings[2],
    justifyContent: "end",
  },
}));
export const delay = (ms: number): Promise<never> => {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
};
export interface AddUserHandle {
  show: (id?: string) => void;
}
export const AddUser = observer(
  React.forwardRef<AddUserHandle, unknown>(function AddUser(props, ref) {
    const [error, setError] = useState<{ error: boolean; message: string }>({ error: false, message: "" });
    const { classes } = useStyles();
    const { authStore } = useStore();
    const [visible, setVisible] = useState<boolean>(false);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [saving, setSaving] = useState<boolean>(false);
    useImperativeHandle(ref, () => {
      return {
        show: show,
      };
    });
    const show = (id?: string) => {
      const user = id ? authStore.GetUserById(id) : null;
      setSelectedUser(user ?? null);
      form.setValues({ name: user?.name ?? "", email: user?.email ?? "", userType: user?.userType.toString() ?? "" });
      setVisible(true);
    };

    const resetState = () => {
      setSelectedUser(null);
      form.setValues({ name: "", email: "", userType: "" });
      setError({ error: false, message: "" });
      setVisible(false);
    };

    const form = useForm({
      initialValues: {
        name: selectedUser?.name ? selectedUser.name : "",
        email: selectedUser?.email ? selectedUser.email : "",
        userType: selectedUser?.userType.toString() ?? "",
      },
      validate: {
        email: (value) => (/^\S+@\S+$/.test(value) ? null : "Invalid email"),
        name: (value) => (value !== "" ? null : "Name is required"),
        userType: (value) => (value != "" ? null : `User Type is required`),
      },
    });

    const createUser = async (values: typeof form.values) => {
      setSaving(true);
      const result =
        selectedUser == null
          ? await authStore.AddUser({ ...values, id: "1", lockoutEnabled: true, userType: parseInt(values["userType"]) })
          : await authStore.UpdateUser({ ...values, id: selectedUser.id ?? "1", lockoutEnabled: true, userType: parseInt(values["userType"]) });
      if (!result.error) {
        setError({ error: false, message: selectedUser?.id != null ? `User Updated Successfully` : `User Created Successfully` });
        await delay(300);
        setSaving(false);
        resetState();
      } else {
        setError({ error: true, message: (result.data as string[]).toLocaleString() });
        setSaving(false);
      }
    };

    const resetError = () => {
      if (error.message !== "") {
        setError({ error: false, message: "" });
      }
    };

    return (
      <Modal
        opened={visible}
        header={selectedUser ? t(`Update ${selectedUser.name}`) : t("Add User")}
        withCloseButton={true}
        onClose={() => {
          resetState();
        }}
        size={"40%"}
      >
        <div className={classes.addUserForm}>
          <form onSubmit={form.onSubmit(createUser)}>
            <TextInput
              label={t("Name")}
              placeholder={t("Name")}
              {...form.getInputProps("name")}
              onChange={(event) => {
                form.setFieldValue("name", event.target.value);
                resetError();
              }}
              withAsterisk
            />
            <TextInput
              label={t("Email")}
              placeholder={t("Email")}
              {...form.getInputProps("email")}
              onChange={(event) => {
                form.setFieldValue("email", event.target.value);
                resetError();
              }}
              withAsterisk
            />
            <Space h={10} />
            <Select
              label={t("User Type")}
              {...form.getInputProps("userType")}
              clearable={false}
              data={[
                { label: "Admin", value: "1" },
                { label: "Premium", value: "0" },
                { label: "Basic", value: "2" },
              ]}
              onChange={(value: string) => {
                form.setFieldValue("userType", value);
                resetError();
              }}
            />
            <Text size={theme.fontSizes?.sm} color={error.error ? "red" : "green"} pt={theme.other?.arc.paddings[2]} align="right">
              {error.message}
            </Text>
            <Group className={classes.userFormFooter}>
              <Button
                variant={"default"}
                onClick={() => {
                  resetState();
                }}
              >
                {t("Cancel")}
              </Button>
              <Button type="submit" loading={saving}>
                {t("Save")}
              </Button>
            </Group>
          </form>
        </div>
      </Modal>
    );
  })
);
